import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNavigation, useNavigationState } from './NavigationProvider';

let sequence = 0;
/** For workflows whose targets/payload live only in the current feature session.
 * Nested navigation preserves them; leaving the workflow expires its descriptor,
 * so Forward cannot apply an old confirmation to a new selection.
 */
export function useTransientNavigationState<T extends string | boolean | null>(
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>, () => boolean] {
  const [descriptor, setDescriptor] = useNavigationState<{
    token: number;
    value: T;
  } | null>(key, null);
  const { replaceState } = useNavigation();
  const live = useRef<number | null>(null);
  const valid = descriptor !== null && descriptor.token === live.current;
  const value = valid ? descriptor.value : initial;
  const latest = useRef(value);
  latest.current = value;
  useEffect(() => {
    if (!descriptor) live.current = null;
    else if (!valid) replaceState(key, null);
  }, [descriptor, valid, key, replaceState]);
  const token = descriptor?.token;
  const isCurrent = useCallback(
    () => token !== undefined && live.current === token,
    [token],
  );
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (update) => {
      const next =
        typeof update === 'function' ? update(latest.current) : update;
      latest.current = next;
      if (next === initial) {
        // A completion from an expired session must not close a newer workflow.
        if (isCurrent()) setDescriptor(null);
      } else {
        live.current = ++sequence;
        setDescriptor({ token: live.current, value: next });
      }
    },
    [initial, setDescriptor, isCurrent],
  );
  return [value, setValue, isCurrent];
}
